import { Paper, Divider, Skeleton } from "@mui/material";

import { Box } from "@mui/system";
import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { FormattedMessage } from "react-intl";

import SubscribedItem, { CARD_PAPER_STYLE } from "./subscribed-item";
import PendingItem from "./pending-item";
import CrossSellItem from "./cross-sell-item";
import BaseFormCard from "../../base/BaseFormCard";
import APIContext from "../../../services/api/api";
import { EcUserDataContext, EcAuthContext } from "@eclear-ag/ec-common-ui";
import ManageProfileContext from "../../../services/manage-profile/manageProfile";
import {
  getCrossSellProducts,
  getPendingItems,
  getPendingRedirectUrl,
  getPendingStatusConfig,
  getSubscriptionDetailPath,
  getUpgradeWizardPath,
  translateBackendProductName,
  PRODUCT_CONFIG
} from "./subscription-product-config";
import messages_de from "../../../translations/de.json";
import messages_en from "../../../translations/en.json";
import UserContext from "../../../services/user/user";
import * as _ from "lodash";

const messages = {
  de: messages_de,
  en: messages_en
};

export default function SubscriptionsList() {
  const { lang } = useContext(UserContext);
  const { setCurrentSubscriptionView } = useContext(APIContext);
  const { getMeData, isFetchingGetMeData, checkGetMeData } = useContext(
    EcUserDataContext
  );
  const { productStatusInfos } = useContext(EcAuthContext);
  const { setSubsciptionDetails } = useContext(ManageProfileContext);
  const navigate = useNavigate();

  // get-me is on-demand (ec-common-ui no longer auto-fires it), so this is
  // the component that needs userSubscriptions and must request it itself.
  // checkGetMeData() no-ops if data is already populated or a fetch is
  // already in-flight, so this is safe even if other components also call it.
  useEffect(() => {
    checkGetMeData?.();
  }, [checkGetMeData]);

  const allSubscriptions = getMeData?.userSubscriptions || [];

  const data = allSubscriptions.filter((item) => item?.status === "ENABLED");

  const pendingItems = getPendingItems(productStatusInfos);

  // Cross-sell (BO-869): offered to every merchant, trial included. Suppressed
  // while get-me is in flight so the section does not flash in and out as the
  // data arrives, and so ownership is computed from real data rather than an
  // empty list.
  const crossSellProducts = isFetchingGetMeData
    ? []
    : getCrossSellProducts(allSubscriptions);

  const ListLoading = () => {
    return (
      <Paper
        data-testid="subscriptions-list-loading-skeleton"
        elevation={0}
        animation="wave"
        style={{
          ...CARD_PAPER_STYLE,
          padding: 16,
          width: "100%",
          marginBottom: 14
        }}
      >
        <Skeleton width="20%" height={18} animation="wave" />
        <Skeleton
          width="40%"
          height={18}
          style={{ marginTop: "20px" }}
          animation="wave"
        />
        <Skeleton
          width="40%"
          height={18}
          style={{ marginTop: "10px" }}
          animation="wave"
        />
      </Paper>
    );
  };

  return (
    <Box data-testid="subscriptions-list" style={{ padding: 0 }}>
      <BaseFormCard
        noForm
        title="products"
        titleProps={{ "data-testid": "subscriptions-list-header" }}
      >
        <Box data-testid="subscriptions-list-body" sx={{ p: 2 }}>
          {/* ── Active subscriptions ── */}
          {isFetchingGetMeData ? (
            <React.Fragment>
              <ListLoading />
              <ListLoading />
            </React.Fragment>
          ) : data?.length > 0 ? (
            data?.map((item, idx) => {
              return (
                <SubscribedItem
                  key={idx}
                  itemId={idx}
                  description={translateBackendProductName(
                    _.get(item, "name", "n/a"),
                    messages[lang] ?? messages.en
                  )}
                  price={_.get(item, "product.price", "-")}
                  billingPeriodType={_.get(item, "product.billingPeriodType", "N/A")}
                  paymentMethodDefault={_.get(item, "paymentMethod", "-")}
                  isFreeTrial={_.get(item, "product.trial", false)}
                  onClick={() => {
                    setCurrentSubscriptionView({
                      screen: "subscriptionDetail",
                      product: _.get(item, "description", "-")
                    });
                    setSubsciptionDetails(item);
                    navigate(getSubscriptionDetailPath(item?.productType));
                  }}
                />
              );
            })
          ) : (
            <span data-testid="subscriptions-list-no-data">
              <FormattedMessage id="noData" />
            </span>
          )}

          {/* ── Pending subscriptions ── */}
          {pendingItems.length > 0 && (
            <Box data-testid="subscriptions-list-pending-section">
              <Divider
                data-testid="subscriptions-list-pending-divider"
                sx={{ borderWidth: "1px", mt: 3, mb: 3 }}
              />
              <Box style={{ height: "40px", marginBottom: "16px" }}>
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    fontStretch: "normal",
                    fontStyle: "normal",
                    lineHeight: 1.45,
                    letterSpacing: "normal",
                    textAlign: "left",
                    opacity: 0.8
                  }}
                >
                  <FormattedMessage id="subscription.pendingSection" />
                </span>
              </Box>
              {pendingItems.map(({ productType, status }, idx) => {
                const statusCfg = getPendingStatusConfig(productType, status);
                const productCfg = PRODUCT_CONFIG[productType];
                return (
                  <PendingItem
                    key={`${productType}-${status}`}
                    itemId={idx}
                    nameKey={productCfg.nameKey}
                    descriptionKey={statusCfg.descriptionKey}
                    redirectUrl={getPendingRedirectUrl(productType, status)}
                  />
                );
              })}
            </Box>
          )}

          {/* ── Cross-selling: products this merchant does not own yet ── */}
          {crossSellProducts.length > 0 && (
            <Box data-testid="subscriptions-list-cross-sell-section">
              <Divider
                data-testid="subscriptions-list-cross-sell-divider"
                sx={{ borderWidth: "1px", mt: 3, mb: 3 }}
              />
              <Box style={{ height: "40px", marginBottom: "16px" }}>
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    fontStretch: "normal",
                    fontStyle: "normal",
                    lineHeight: 1.45,
                    letterSpacing: "normal",
                    textAlign: "left",
                    opacity: 0.8
                  }}
                >
                  <FormattedMessage id="subscription.crossSellSection" />
                </span>
              </Box>
              {crossSellProducts.map((productType) => (
                <CrossSellItem
                  key={productType}
                  productType={productType}
                  nameKey={PRODUCT_CONFIG[productType].nameKey}
                  descriptionKey={
                    PRODUCT_CONFIG[productType].crossSellDescriptionKey
                  }
                  onClick={() => navigate(getUpgradeWizardPath(productType))}
                />
              ))}
            </Box>
          )}
        </Box>
      </BaseFormCard>

      <Box
        data-testid="subscriptions-list-support"
        sx={{
          mt: "20px",
          fontFamily: "Inter",
          fontSize: "16px",
          fontWeight: "normal",
          lineHeight: 1.5,
          textAlign: "left",
          color: "var(--font-strong-default)"
        }}
      >
        <FormattedMessage id="supportMessage" />
        <a
          href="mailto:support@eclear.com"
          style={{ color: "var(--font-strong-default)" }}
        >
          support@eclear.com
        </a>
      </Box>
    </Box>
  );
}
